import React, { FC, useState, useEffect } from 'react';
import { Box, Grid, Typography, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InfoIcon from '@mui/icons-material/Info';
import PrintIcon from '@mui/icons-material/Print';

import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import { useForecastAnalysis, useXlsDownload } from './hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../../common/api/request';
import ForecastStratumTable from './components/ForecastStratumTable';

import { 
  MainContent, 
  OverviewContainer, 
  TableSection,
  ActionButton
} from './ForecastStratum.styled';

const ForecastStratum: FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [guidelinesModalOpen, setGuidelinesModalOpen] = useState<boolean>(false);
  const [stratumSizesCalculated, setStratumSizesCalculated] = useState<boolean>(false);
  const [currentTableData, setCurrentTableData] = useState<any>(null);
  
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/stratum-sizes-sampling_rates', '');
  const { data: userData, isLoading: userLoading } = useUser();
  const queryClient = useQueryClient();

  // Mutation for calculating stratum sizes
  const stratumSizesMutation = useMutation({
    mutationFn: async () => {
      // Make POST request to calculate stratum sizes
      const postResponse = await axios.post('/forecast-stratum/sampling-rates');
      
      // Always make the GET request to refresh data
      const getResponse = await axios.get('/forecast-stratum');
      
      return {
        postData: postResponse.data,
        getData: getResponse.data
      };
    },
    onSuccess: (data) => {
      setStratumSizesCalculated(true);
      setCurrentTableData(data.getData);
      setSnackbar({
        open: true,
        message: 'Stratum sizes calculated successfully',
        severity: 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: 'Failed to calculate stratum sizes',
        severity: 'error',
      });
    },
  });

  const forecastAnalysisMutation = useForecastAnalysis({
    onSuccess: (data) => {
      setCurrentTableData(data.getData);
      setSnackbar({
        open: true,
        message: 'Forecast analysis completed successfully',
        severity: 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: 'Failed to run forecast analysis',
        severity: 'error',
      });
    },
  });

  const xlsDownloadMutation = useXlsDownload({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'XLS report downloaded successfully',
        severity: 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: 'Failed to download XLS report',
        severity: 'error',
      });
    },
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleGuidelinesOpen = () => {
    setGuidelinesModalOpen(true);
  };

  const handleGuidelinesClose = () => {
    setGuidelinesModalOpen(false);
  };

  const handlePrintGuidelines = () => {
    window.print();
  };

  const handleCalculateStratumSizes = () => {
    setSnackbar({
      open: true,
      message: 'Calculating stratum sizes...',
      severity: 'info',
    });
    stratumSizesMutation.mutate();
  };

  const handleDownloadHtml = () => {
    setSnackbar({
      open: true,
      message: 'Generating HTML report...',
      severity: 'info',
    });
    forecastAnalysisMutation.mutate({
      type: 'html',
      stratumSizesCalculated
    });
  };

  const handleDownloadXls = () => {
    setSnackbar({
      open: true,
      message: 'Generating XLS report...',
      severity: 'info',
    });
    xlsDownloadMutation.mutate({
      stratumSizesCalculated
    });
  };

  const showLoading = permissionLoading || userLoading;
  const isAnyMutationPending = stratumSizesMutation.isPending || forecastAnalysisMutation.isPending || xlsDownloadMutation.isPending;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='forecast-stratum-container'>
      <Grid container data-testid="forecast-stratum-grid-container">
        {/* First Row - SectionBar */}
        <Grid item xs={12} data-testid="forecast-stratum-section-bar-container">
          <SectionBar
            title='Forecast Stratum Size & Sampling Rates'
            subtitle='Sampling Planning'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            data-testid="forecast-stratum-section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }} data-testid="forecast-stratum-main-row">
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid="forecast-stratum-sidebar-container">
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="forecast-stratum-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='forecast-stratum-main-content'>
            <OverviewContainer data-testid="forecast-stratum-overview-container">
              {!hasPermission ? (
                <Alert severity='warning' data-testid="forecast-stratum-permission-error-alert">
                  You don't have permission to view this section
                </Alert>
              ) : (
                <TableSection data-testid="forecast-stratum-content-section">
                  {/* Page Title */}
                  <Box sx={{ mb: 3 }} data-testid="forecast-stratum-guidelines-section">
                    <Button
                      variant="outlined"
                      startIcon={<InfoIcon />}
                      onClick={handleGuidelinesOpen}
                      sx={{ mb: 3 }}
                      data-testid="forecast-stratum-guidelines-button"
                    >
                      Guidelines to the use of the forecasted sampling rates
                    </Button>
                  </Box>

                  {/* Calculate Sections - Inline Layout */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }} data-testid="forecast-stratum-actions-section">
                    {/* Calculate Stratum Sizes Section */}
                    <Box sx={{ flex: 1, maxWidth: '250px' }} data-testid="forecast-stratum-calculate-section">
                      <ActionButton
                        variant="contained"
                        onClick={handleDownloadHtml}
                        fontSize="13px"
                        disabled={isAnyMutationPending}
                        startIcon={isAnyMutationPending ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null}
                        data-testid="forecast-stratum-calculate-stratum-sizes-button"
                      >
                        {'Calculate Stratum Sizes'}
                      </ActionButton>
                    </Box>

                    {/* Calculate Sampling Rates Section */}
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }} data-testid="forecast-stratum-sampling-rates-section">
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }} data-testid="forecast-stratum-sampling-rates-title">
                        Calculate Sampling Rates
                      </Typography>
                      
                      <ActionButton
                        variant="contained"
                        onClick={handleCalculateStratumSizes}
                        disabled={isAnyMutationPending}
                        startIcon={isAnyMutationPending ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null}
                        data-testid="forecast-stratum-download-html-button"
                      >
                        {'HTML'}
                      </ActionButton>
                      
                      <ActionButton
                        variant="contained"
                        onClick={handleDownloadXls}
                        disabled={isAnyMutationPending}
                        startIcon={isAnyMutationPending ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null}
                        data-testid="forecast-stratum-download-xls-button"
                      >
                        {'XLS'}
                      </ActionButton>
                    </Box>
                  </Box>

                  {/* Forecast Stratum Table */}
                  {currentTableData && (
                    <Box sx={{ mt: 3 }} data-testid="forecast-stratum-table-container">
                      <ForecastStratumTable 
                        data={currentTableData}
                        onUpdateSuccess={(refreshedData) => {
                          setCurrentTableData(refreshedData);
                          setSnackbar({
                            open: true,
                            message: 'Forecast updated successfully',
                            severity: 'success',
                          });
                        }}
                        onUpdateError={(error) => {
                          setSnackbar({
                            open: true,
                            message: 'Failed to update forecast',
                            severity: 'error',
                          });
                        }}
                        data-testid="forecast-stratum-table"
                      />
                    </Box>
                  )}
                </TableSection>
              )}
            </OverviewContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Guidelines Modal */}
      <Dialog
        open={guidelinesModalOpen}
        onClose={handleGuidelinesClose}
        maxWidth="md"
        fullWidth
        data-testid="forecast-stratum-guidelines-modal"
      >
        <DialogTitle data-testid="forecast-stratum-guidelines-modal-title">
          <Typography variant="h6" component="h2" data-testid="forecast-stratum-guidelines-modal-title-text">
            Guidelines to the use of the forecasted sampling rates
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Description of the methodology
          </Typography>
          
          <Typography variant="body1" paragraph>
            The sampling planning module provides an estimation of the sample size for each stratum. The calculations are done in two steps:
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            1. First we need to estimate the expected number of received receptacles for the next quarter of each stratum ("stratum size").
          </Typography>
          
          <Typography variant="body1" paragraph>
            In order to do this forecast historical data of the stratum sizes must be available in iPep. This information is provided in the normal eNO1 files in the column "received_receptacles_stratum". The more historical data we have the better. It is then easier to detect patterns or periodicities in the time series of the data that can be projected into the future.
          </Typography>

          <Typography variant="body1" paragraph>
            The minimum requirement is to have data of the last four quarters. It is obvious that the current quarter is the most important for the forecast of the next quarter. So the stratum sizes of the current quarter must be available in any case.
          </Typography>

          <Typography variant="body1" paragraph>
            As it is not allowed to upload an eNO1 file to iPep before the end of the period it refers to, such incomplete (so-called "temporary") eNO1 files can only be uploaded under "eNO1 Temp Upload". A temporary eNO1 file of the current quarter cannot be complete by definition (because the current quarter is not over yet). Therefore iPep provides the possibility to "extrapolate" an incomplete temporary eNO1 file to the full quarter. If this option is chosen then the received weights and the received numbers of receptacles per stratum are automatically adjusted (increased) so that they represent the whole quarter rather than the time period from the beginning of the quarter until the generation date of the file.
          </Typography>

          <Typography variant="body1" paragraph>
            In this step only the stratum sizes are needed, so it is also possible to upload "reduced temporary" eNO1 files for the current quarter that basically only contain the stratum size information. Automatic extrapolation of reduced temporary files to the full quarter is not possible. The user must take this into account when generating the data.
          </Typography>

          <Typography variant="body1" paragraph>
            Once the stratum sizes of the four (or more) previous quarters are available iPep uses statistical time series analysis in order to predict the stratum sizes of the next quarter. The whole time series is shown as a graph. In addition to that the stratum size value of the current quarter and the forecast for the next quarter are listed in the forecast table.
          </Typography>

          <Typography variant="body1" paragraph>
            From the graph the user can get an impression how "reasonable" the forecast is. It can be seen whether trends and/or periodicities were found and projected into the future. If the time series is short and/or without any regular pattern then the forecast could be just a kind of an average value of the past.
          </Typography>

          <Typography variant="body1" paragraph>
            If the user finds that the forecast is not reasonable or if he has additional information about the continuation of a flow (e.g. knowledge that a new flow is added to or removed from a stratum, or that a flow that had been dispatched in bags will be dispatched on pallets etc.) he has the possibility to overrule iPep's stratum size forecast by entering his/her own stratum size forecast value. This value will be used for further calculations.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            2. Secondly we need to know the number of sampled receptacles that are required in order to meet the accuracy targets.
          </Typography>

          <Typography variant="body1" paragraph>
            The standard accuracy targets (according to the operational guide) per DO, service and format are pre-set in the "Settings" section of the SPM. They can be overruled manually by the user.
          </Typography>

          <Typography variant="body1" paragraph>
            iPep uses the sampling data of the four previous quarters (only full quarters with complete sampling data, i.e. no temp_red files and no data of the current quarter) and calculates the variances, the achieved accuracies and the required sample sizes for each DO, service and format. Under the assumption that the distribution of the variances of the strata of the past is valid in the future, too, the total expected sample size (DO, service, format) is divided accordingly in order to get the sample sizes per stratum. This procedure is called the "optimum allocation".
          </Typography>

          <Typography variant="body1" paragraph>
            Now it is easy to calculate the sampling rate as the predicted stratum size for the next quarter divided by the expected sample size of a stratum.
          </Typography>

          <Typography variant="body1" paragraph>
            The "minimum requirements" are taken into account in iPep's calculations, i.e. the minimum sample sizes of 30 per stratum (or 40 for stratum sizes &gt; 1000) and the minimum sampling rate of 3.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
            Interpretation and application of the forecasts
          </Typography>

          <Typography variant="body2" component="div">
            <ul>
              <li>After the calculation of the stratum sizes the user must assess whether the stratum size forecasts are reasonable by evaluating the graphs and by potentially taking additional information about the flows into account. The calculated forecasts should be overwritten manually if they are found to be inappropriate.</li>
              <li>The calculated sampling rates are only indicative and must not be taken as the absolute truth. Many assumptions and uncertainties are part of the calculations that might not be perfectly matching the future development.</li>
              <li>iPep's calculations just fulfill the minimum requirements with regards to sample sizes and accuracy. It might therefore be wise to apply lower sampling rates than proposed by iPep in order to be on the safe side. Alternatively the accuracy targets can be set more strictly (lower) than required by the operational guide. This also leads to lower sampling rates.</li>
              <li>In order to optimize the sampling costs it is recommended to adjust the sampling rates every quarter. This requires to apply the SPM every three months a few days before the end of a quarter.</li>
              <li>The SPM only calculates sampling rates at stratum level. This is btw. the most efficient and least costly way (in terms of sampling cost). If sampling rates are to be set at a different level (e.g. at the level DO-service-format) the SPM does not provide the appropriate information.</li>
              <li>The whole forecast table including the predicted sampling rates (but without the graphs) can be downloaded as an Excel file for further processing, e.g. as the basis for an upload of the sampling rates to the IT system that manages the sample selection rules.</li>
              <li>Neither iPep nor IPC nor the developers or the OTG can be held responsible if iPep's sampling rates forecasts lead to insufficient sample sizes or missed accuracy targets. Each DO keeps on being responsible for its sampling results. iPep's SPM is only a supporting tool.</li>
            </ul>
          </Typography>

          <Typography variant="body1" sx={{ mt: 3, fontStyle: 'italic', textAlign: 'center' }}>
            REIMS/Interconnect Sampling Subgroup, May 2017
          </Typography>
        </DialogContent>
        <DialogActions data-testid="forecast-stratum-guidelines-modal-actions">
          <Button 
            onClick={handlePrintGuidelines} 
            variant="outlined"
            startIcon={<PrintIcon />}
            sx={{ mr: 'auto' }}
            data-testid="forecast-stratum-print-guidelines-button"
          >
            Print
          </Button>
          <Button onClick={handleGuidelinesClose} variant="contained" data-testid="forecast-stratum-close-guidelines-button">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="forecast-stratum-notification-snackbar"
      />
    </Box>
  );
};

export default ForecastStratum; 